/**
 * DrillAwareTooltip — shared chart-tooltip primitive.
 *
 * Renders the standard GoodData-style hover tooltip:
 *   - Header line (category / x value)
 *   - Series rows with colored swatches + label + value
 *   - "Click to drill or filter" affordance footer
 *
 * Every chart implementation (Recharts/Visx/D3/ECharts) renders this same DOM
 * via portals or custom tooltip props, so the lib swap doesn't change tooltip
 * appearance.
 */

import { cn } from '@/lib/utils';

interface TooltipRow {
  /** Series/category color swatch. */
  color: string;
  /** Series label. */
  label: string;
  /** Numeric or pre-formatted value. */
  value: number | string;
  /** Optional unit suffix. */
  unit?: string;
}

interface DrillAwareTooltipProps {
  /** Header — typically the x value (e.g., "Q3 2026", "US", "192.168.1.0/24"). */
  header?: string;
  /** Rows — one per series. */
  rows: TooltipRow[];
  /** Hide the drill affordance footer (default: shown). */
  hideDrillHint?: boolean;
  /** Override the footer text. Default: "Click to drill or filter". */
  drillHint?: string;
  className?: string;
}

export function DrillAwareTooltip({
  header,
  rows,
  hideDrillHint,
  drillHint = 'Click to drill or filter',
  className,
}: DrillAwareTooltipProps) {
  return (
    <div
      className={cn(
        'dls-bg-popover dls-text-popover-foreground dls-border dls-border-border dls-rounded-md dls-shadow-md dls-px-3 dls-py-2 dls-text-xs dls-min-w-[160px]',
        className
      )}
      role="tooltip"
    >
      {header && (
        <div className="dls-font-semibold dls-text-foreground dls-pb-1.5 dls-mb-1.5 dls-border-b dls-border-border">
          {header}
        </div>
      )}
      <div className="dls-flex dls-flex-col dls-gap-1">
        {rows.map((row, i) => (
          <div key={`${row.label}-${i}`} className="dls-flex dls-items-center dls-gap-2">
            <span
              className="dls-h-2.5 dls-w-2.5 dls-rounded-sm dls-flex-shrink-0"
              style={{ backgroundColor: row.color }}
              aria-hidden
            />
            <span className="dls-text-muted-foreground dls-flex-1 dls-truncate">{row.label}</span>
            <span className="dls-font-medium dls-text-foreground">
              {typeof row.value === 'number' ? row.value.toLocaleString() : row.value}
              {row.unit ?? ''}
            </span>
          </div>
        ))}
      </div>
      {!hideDrillHint && (
        <div className="dls-mt-2 dls-pt-1.5 dls-border-t dls-border-border dls-text-[10px] dls-text-muted-foreground dls-italic">
          {drillHint}
        </div>
      )}
    </div>
  );
}
